import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteCustomerPayment,
  getCustomerPaymentById,
  getCustomerPayments,
  getCustomerPaymentTotals,
  updateCustomerPayment,
} from "@/features/customer-payments/hooks/customer-payments-client";
import { getCustomerPaymentRelated } from "@/features/customer-payments/hooks/customer-payment-related";
import type {
  CustomerPayment,
  CustomerPaymentFilters,
  CustomerPaymentUpdateInput,
} from "@/features/customer-payments/types";
import { invalidateCustomerPaymentQueries } from "@/features/finance/hooks/customer-payment-cache";
import { paymentKeys } from "@/features/customer-payments/hooks/query-keys";

export function useCustomerPayment(paymentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: paymentKeys.customer.detail(paymentId),
    queryFn: () => getCustomerPaymentById(paymentId),
    enabled: options?.enabled !== false && Boolean(paymentId),
  });
}

/**
 * The relationship graph hanging off an already-loaded payment.
 *
 * Takes the payment rather than its id so the graph is built from the row
 * `useCustomerPayment` already has. Passing an id meant this query refetched
 * the same payment under a different key, where TanStack Query's deduplication
 * could not see it — two reads of one row on every detail and edit view.
 */
export function useCustomerPaymentRelated(payment: CustomerPayment | undefined) {
  return useQuery({
    queryKey: paymentKeys.customer.related(payment?.id ?? ""),
    queryFn: () => getCustomerPaymentRelated(payment as CustomerPayment),
    enabled: Boolean(payment),
  });
}

export function useCustomerPayments(filters: CustomerPaymentFilters = {}) {
  const normalizedFilters: CustomerPaymentFilters = {
    ...filters,
    limit: filters.limit ?? 50,
    offset: Math.max(0, filters.offset ?? 0),
  };

  const queryKeyFilters: Record<string, unknown> = {
    limit: normalizedFilters.limit,
    offset: normalizedFilters.offset,
  };

  if (normalizedFilters.invoice_id) {
    queryKeyFilters.invoice_id = normalizedFilters.invoice_id;
  }
  if (normalizedFilters.account_id) {
    queryKeyFilters.account_id = normalizedFilters.account_id;
  }
  if (normalizedFilters.search?.trim()) {
    queryKeyFilters.search = normalizedFilters.search.trim();
  }
  if (normalizedFilters.sort_field) {
    queryKeyFilters.sort_field = normalizedFilters.sort_field;
    queryKeyFilters.sort_direction = normalizedFilters.sort_direction ?? "desc";
  }

  return useQuery({
    queryKey: paymentKeys.customer.list(queryKeyFilters),
    queryFn: () => getCustomerPayments(normalizedFilters),
  });
}

/**
 * Count and total received for the whole filtered set.
 *
 * Paging and ordering are deliberately excluded from the key: the totals
 * describe the whole filtered set, so including them would refetch an
 * identical aggregate on every page step and cache it under a different key
 * each time.
 */
export function useCustomerPaymentTotals(filters: CustomerPaymentFilters = {}) {
  const totalsFilters: CustomerPaymentFilters = {
    account_id: filters.account_id,
    invoice_id: filters.invoice_id,
    search: filters.search,
  };

  return useQuery({
    queryKey: paymentKeys.customer.totals(totalsFilters as Record<string, unknown>),
    queryFn: () => getCustomerPaymentTotals(totalsFilters),
  });
}

export function useDeleteCustomerPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: string) => deleteCustomerPayment(paymentId),
    onSuccess: () => invalidateCustomerPaymentQueries(queryClient),
  });
}

interface UpdateCustomerPaymentVariables {
  input: CustomerPaymentUpdateInput;
  paymentId: string;
}

/**
 * Corrects a recorded payment in place.
 *
 * An edit moves the same caches a create or delete does, so it shares the
 * canonical invalidator rather than repeating the key list. `paymentKeys
 * .customer.all` prefix-matches this payment's detail key and the relationship
 * graph nested under it, so neither needs naming here.
 */
export function useUpdateCustomerPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ input, paymentId }: UpdateCustomerPaymentVariables) =>
      updateCustomerPayment(paymentId, input),
    onSuccess: () => invalidateCustomerPaymentQueries(queryClient),
  });
}
